import { useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { getBasePaySchedulesQuery } from 'components/hooks/payroll/utils'
import { Button, ButtonGroup, useBreakpoint } from 'slab'

import { BankClosureStrategyInput } from './BankClosureStrategyInput'
import { PayFrequencyField, PayScheduleDateFields, PayScheduleNameField } from './FormFields'
import { PayDateFields } from './PayDateFields'
import { PayDatePreview } from './PayDatePreview'
import { type BankClosureStrategy, type PayFrequency, PayFrequencyLabels, type PayScheduleFormInputs } from '../../types/payroll'
import { useCreatePaySchedule } from '../hooks/useCreatePaySchedule'
import { useGetSamplePayPeriods } from '../hooks/useGetSamplePayPeriods'
import { Banner } from '../reusable/Banner'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet'

interface AddPayScheduleSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddPayScheduleSheet ({ open, onOpenChange }: AddPayScheduleSheetProps) {
  const queryClient = useQueryClient()
  const createPayScheduleMutation = useCreatePaySchedule()
  const [bannerError, setBannerError] = useState<string>()
  const currentYear = new Date().getFullYear()

  const {
    control,
    getValues,
    formState: { isSubmitting, errors, isValid },
    handleSubmit,
    register,
    setValue,
    watch,
    trigger,
    reset
  } = useForm<PayScheduleFormInputs>({
    mode: 'all',
    defaultValues: {
      frequency: '' as PayFrequency,
      name: '',
      bankClosureStrategy: '' as BankClosureStrategy,
      endDate: undefined,
      firstDay: undefined,
      secondDay: undefined,
      examplePayDate: undefined
    }
  })

  const onSubmit = async (data: PayScheduleFormInputs) => {
    if (isValid) {
      createPayScheduleMutation.mutate({
        frequency: data.frequency,
        name: data.name,
        bank_closure_strategy: data.bankClosureStrategy,
        first_day: data.firstDay,
        second_day: data.secondDay,
        reference_pay_date: data.examplePayDate,
        end_date: data.endDate || null
      }, {
        onSuccess: () => {
          toast.success('Pay schedule created successfully')
          queryClient.invalidateQueries({ queryKey: getBasePaySchedulesQuery() })
          reset()
          onOpenChange(false)
        },
        onError: (error) => {
          setBannerError(error.errors.join('. ') || error.message)
        }
      })
    }
  }

  const setName = (name: PayFrequency, onChange: React.Dispatch<React.SetStateAction<string>>) => {
    setValue('name', `${PayFrequencyLabels[name]} (${currentYear})`)
    onChange(name)
  }

  const frequency = watch('frequency')

  const { data, isLoading, error } = useGetSamplePayPeriods({
    frequency,
    bank_closure_strategy: watch('bankClosureStrategy'),
    first_day: watch('firstDay'),
    second_day: watch('secondDay'),
    reference_pay_date: watch('examplePayDate'),
    isValid,
  })

  const { breakpointIsAtLeast } = useBreakpoint()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>

      <SheetContent responsiveLayout style={breakpointIsAtLeast('xs') ? { minWidth: '500px' } : {}}>
        <SheetHeader>
          <SheetTitle className='heading-lg'>Add pay schedule</SheetTitle>
        </SheetHeader>
        <SheetDescription className='hidden'>Create a new pay schedule</SheetDescription>
        <hr className='mt-6 mb-10' />
        {bannerError && (
          <Banner
            type='critical'
            titleText='Error creating pay schedule'
            className='my-2'
          >
            {bannerError}
          </Banner>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='space-y-6'>
            <PayFrequencyField
              control={control}
              error={errors.frequency}
              fieldId='frequency'
              customOnChange={setName}
            />

            <PayScheduleNameField
              control={control}
              error={errors.name}
              fieldId='name'
            />

            <PayScheduleDateFields
              control={control}
              errors={errors}
              watch={watch}
            />

            <PayDateFields
              frequency={frequency}
              control={control}
              register={register}
              errors={errors}
              watch={watch}
              trigger={trigger}
              getValues={getValues}
            />

            <BankClosureStrategyInput
              register={register}
              error={errors.bankClosureStrategy}
            />

            {isValid && (
              <PayDatePreview isLoading={isLoading} data={data} error={error?.message} />
            )}
          </div>

          <hr className='mb-6 mt-10' />

          <ButtonGroup size='large'>
            <Button variant='secondary' onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type='submit'
              variant='primary'
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting ? 'Creating...' : 'Create pay schedule'}
            </Button>
          </ButtonGroup>
        </form>
      </SheetContent>
    </Sheet>
  )
}
